import { FC, PropsWithChildren, useMemo } from 'react';
import { create } from 'zustand';
import { StepInfo, MultistepState, MultistepActions, MultistepContext } from './useMultistep';

const canGo = (steps: Record<string, StepInfo>, current: string) => {
  const keys = Object.keys(steps);
  const index = keys.indexOf(current);
  const canGoNext = index <= keys.length - 2;
  const canGoPrevious = index >= 1;
  return { canGoNext, canGoPrevious };
};

const findAppropriateIndex = (steps: Record<string, StepInfo>, current: string) => {
  const keys = Object.keys(steps);
  if (current === keys.at(0)) return keys.at(1);
  if (current === keys.at(-1)) return keys.at(-2);
  const currentIndex = keys.indexOf(current);
  return keys.at(currentIndex + 1);
};

interface MultistepProps {
  onFinish?: () => Promise<void> | void;
}
export const Multistep: FC<PropsWithChildren<MultistepProps>> = ({ children, onFinish }) => {
  const store = useMemo(
    () =>
      create<MultistepState & MultistepActions>((set, get) => ({
        steps: {},
        addStep: step => {
          set(state => {
            const steps = { ...state.steps, [step.id]: step };
            const current = state.current || step.id;
            const canGoRules = canGo(steps, current);
            return { steps: { ...state.steps, [step.id]: step }, current, ...canGoRules };
          });
        },
        removeStep: id =>
          set(state => {
            // extract the step with the id and remove it from the steps object
            const steps = {
              ...state.steps
            };

            delete steps[id];

            const current = state.current || id;
            const next = findAppropriateIndex(steps, current);
            if (!next) return { steps, current: undefined, canGoNext: false, canGoPrevious: false };

            const canGoRules = canGo(steps, next);
            return { steps, current: next, ...canGoRules };
          }),
        skipToStep: id =>
          set(state => {
            const validateId = state.steps[id];
            if (!validateId) {
              throw Error(`No step ${id} exists`);
            }

            const canGoRules = canGo(state.steps, id);
            return { current: id, ...canGoRules };
          }),
        next: async () => {
          const { steps, current } = get();
          const keys = Object.keys(steps);
          // if there is no current step, we set the first step as the current step

          if (!current) {
            throw new Error('No current step');
          }

          const { validate } = steps[current];
          if (validate) {
            const isValid = await validate();
            if (!isValid) return;
          }

          // if the current step is the last step, we do nothing
          const currentIndex = keys.indexOf(current);
          if (currentIndex >= keys.length - 1) return;

          const newIndex = currentIndex + 1;
          const newKey = keys.at(newIndex);

          if (!newKey) throw new Error('No new key');
          // if the current step is not the last step, we set the next step as the current step
          const canGoRules = canGo(steps, newKey);
          set({ current: newKey, ...canGoRules });
        },
        previous: () => {
          const { steps, current } = get();
          const keys = Object.keys(steps);

          // if there is no current step, we set the last step as the current step
          if (!current) throw new Error('No current step');

          // if the current step is the first step, we do nothing
          const currentIndex = keys.indexOf(current);
          if (currentIndex === 0) throw new Error('Current step is the first step');

          const newIndex = currentIndex - 1;
          const newKey = keys.at(newIndex);

          if (!newKey) throw new Error('No new key');

          // if the current step is not the first step, we set the previous step as the current step
          const canGoRules = canGo(steps, newKey);
          set({ current: newKey, ...canGoRules });
        },
        finish: async () => {
          const { steps, current } = get();
          const keys = Object.keys(steps);
          if (!current) throw new Error('No current step');
          if (current !== keys.at(-1)) throw new Error('Current step is not the last step');
          // validate the current step
          const { validate } = steps[current];
          if (validate) {
            const isValid = await validate();
            if (!isValid) return;
          }

          if (onFinish) {
            set({ loading: true });
            try {
              await onFinish();
            } catch (_ex) {
              /* empty */
            }
            set({ loading: false });
          }
        },
        canGoNext: false,
        canGoPrevious: false
      })),
    [onFinish]
  );

  return <MultistepContext.Provider value={store}>{children}</MultistepContext.Provider>;
};
